import React, { useEffect, useState } from "react";
import {
  Stack,
  Button,
  TextInput,
  PasswordInput,
  Text,
  Alert,
  Loader,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconInfoCircle,
  IconCheck,
  IconAlertCircle,
} from "@tabler/icons-react";

import { useUserInvitations } from "@/hooks/useUserInvitations";

interface AcceptInvitationFormData {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

export interface AcceptInvitationFormProps {
  /** The invitation ID from the URL */
  invitationId: string;

  /** Callback function called when user successfully accepts invitation */
  onAcceptSuccess?: (userData: any) => void;

  /** Whether user is authenticated */
  isAuthenticated?: boolean;
}

export const AcceptInvitationForm: React.FC<AcceptInvitationFormProps> = ({
  invitationId,
  onAcceptSuccess,
  isAuthenticated = false,
}) => {
  const [serverErrors, setServerErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState<string>("");

  const {
    userInvitation,
    loading: loadingInvitation,
    isAcceptingInvitation,
    get,
    acceptInvitation,
    lastError,
  } = useUserInvitations({
    autoLoad: false,
    isAuthenticated,
  });

  const form = useForm<AcceptInvitationFormData>({
    initialValues: {
      username: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
    validate: {
      username: (value) => {
        if (!value) return "Username is required";
        if (value.length < 3) return "Username must be at least 3 characters";
        if (!/^[a-zA-Z0-9_-]+$/.test(value))
          return "Username can only contain letters, numbers, hyphens, and underscores";
        return null;
      },
      firstName: (value) => {
        if (!value) return "First name is required";
        if (value.length < 2) return "First name must be at least 2 characters";
        return null;
      },
      lastName: (value) => {
        if (!value) return "Last name is required";
        if (value.length < 2) return "Last name must be at least 2 characters";
        return null;
      },
      email: (value) => {
        if (!value) return "Email is required";
        if (!/^\S+@\S+\.\S+$/.test(value)) return "Invalid email format";
        return null;
      },
      phone: (value) => {
        // Phone is optional, but if provided, validate format
        if (value && !/^\+?[\d\s()-]{10,}$/.test(value)) {
          return "Invalid phone number format";
        }
        return null;
      },
      password: (value) => {
        if (!value) return "Password is required";
        if (value.length < 8) return "Password must be at least 8 characters";
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          return "Password must contain at least one uppercase letter, one lowercase letter, and one number";
        }
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value) return "Please confirm your password";
        if (value !== values.password) return "Passwords do not match";
        return null;
      },
    },
  });

  // Load invitation details on mount
  useEffect(() => {
    if (invitationId) {
      get(invitationId);
    }
  }, [invitationId, get]);

  // Pre-fill form when invitation data is loaded
  useEffect(() => {
    if (userInvitation) {
      form.setValues({
        firstName: userInvitation.firstName || "",
        lastName: userInvitation.lastName || "",
        email: userInvitation.email || "",
        username: form.values.username,
        phone: form.values.phone,
        password: form.values.password,
        confirmPassword: form.values.confirmPassword,
      });
    }
  }, [userInvitation]);

  // Check if invitation is expired
  const isInvitationExpired = () => {
    if (!userInvitation?.expiresAt) return false;
    return new Date(userInvitation.expiresAt) < new Date();
  };

  const handleSubmit = async (values: AcceptInvitationFormData) => {
    setServerErrors({});
    setSuccessMessage("");

    // Check expiration before submitting
    if (isInvitationExpired()) {
      setServerErrors({
        general:
          "This invitation has expired. Please request a new invitation.",
      });
      return;
    }

    try {
      const result = await acceptInvitation({
        id: invitationId,
        username: values.username,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone || null,
        password: values.password,
        repeatPassword: values.confirmPassword,
      });

      if (result) {
        setSuccessMessage("Invitation accepted successfully! Redirecting...");

        // Notify parent component that acceptance was successful
        setTimeout(() => {
          onAcceptSuccess?.(result);
        }, 1000);
      } else {
        // If result is null, there should be an error in lastError
        setServerErrors({
          general:
            lastError ||
            "Failed to accept invitation. Please try again or contact support.",
        });
      }
    } catch (error) {
      console.error("Accept invitation error:", error);
      setServerErrors({
        general:
          error instanceof Error
            ? error.message
            : "An error occurred while accepting the invitation. Please try again.",
      });
    }
  };

  // Show loading state while fetching invitation
  if (loadingInvitation) {
    return (
      <Center p="xl">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading invitation details...</Text>
        </Stack>
      </Center>
    );
  }

  // Show error if invitation not found
  if (!loadingInvitation && !userInvitation) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {lastError ||
          "Invitation not found or has been revoked. Please contact your administrator."}
      </Alert>
    );
  }

  // Show error if invitation is expired
  if (isInvitationExpired()) {
    return (
      <Alert color="orange" icon={<IconInfoCircle size={16} />}>
        This invitation has expired. Please request a new invitation from your
        administrator.
      </Alert>
    );
  }

  // Show success state
  if (successMessage) {
    return (
      <Alert color="green" icon={<IconCheck size={16} />}>
        {successMessage}
      </Alert>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {serverErrors.general && (
          <Alert color="red" icon={<IconInfoCircle size={16} />}>
            {serverErrors.general}
          </Alert>
        )}

        <TextInput
          label="First Name"
          placeholder="Enter your first name"
          required
          {...form.getInputProps("firstName")}
          error={form.errors.firstName || serverErrors.firstName}
        />

        <TextInput
          label="Last Name"
          placeholder="Enter your last name"
          required
          {...form.getInputProps("lastName")}
          error={form.errors.lastName || serverErrors.lastName}
        />

        <TextInput
          label="Email"
          placeholder="Enter your email"
          description="This email was used to send you the invitation"
          type="email"
          required
          {...form.getInputProps("email")}
          error={form.errors.email || serverErrors.email}
        />

        <TextInput
          label="Username"
          placeholder="Choose a username"
          description="This will be used to sign in to your account"
          required
          {...form.getInputProps("username")}
          error={form.errors.username || serverErrors.username}
        />

        <TextInput
          label="Phone Number"
          placeholder="Enter your phone number (optional)"
          description="Optional - for account recovery"
          type="tel"
          {...form.getInputProps("phone")}
          error={form.errors.phone || serverErrors.phone}
        />

        <PasswordInput
          label="Password"
          placeholder="Create a password"
          description="Must be at least 8 characters with uppercase, lowercase, and number"
          required
          {...form.getInputProps("password")}
          error={form.errors.password || serverErrors.password}
        />

        <PasswordInput
          label="Confirm Password"
          placeholder="Confirm your password"
          required
          {...form.getInputProps("confirmPassword")}
          error={form.errors.confirmPassword || serverErrors.confirmPassword}
        />

        <Button
          type="submit"
          fullWidth
          size="md"
          mt="md"
          loading={isAcceptingInvitation}
        >
          Accept Invitation & Create Account
        </Button>
      </Stack>
    </form>
  );
};
